import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DollarSign, TrendingUp, Target, Zap, Award, Rocket } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import AnimatedCounter from '../components/AnimatedCounter';
import PartieBadge from '../components/PartieBadge';
import AcronymTooltip from '../components/AcronymTooltip';
import { useLanguage } from '../contexts/LanguageContext';
import { yearlyTotals, objectifs, TAM_TOTAL, preLaunchBudget, valorisation, SEZZLE_NET, APP_PRIX, SAAS_PRIX, tauxParAnnee, LICORNE_YEAR, preSeedInvestment, objectifsY1, tractionActuelle } from '../mock';
import { formatCurrency, formatCompact, formatCompactMobile } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';

export default function ResumeExecutifPage() {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const fmt = (v) => isMobile ? formatCompactMobile(v) : formatCompact(v);
  const total5Years = yearlyTotals.reduce((acc, year) => ({
    revenus: acc.revenus + year.revenus,
    baiia: acc.baiia + year.baiia,
    gmv: acc.gmv + year.gmvRegulier + year.gmvSezzle
  }), { revenus: 0, baiia: 0, gmv: 0 });

  const lastYear = yearlyTotals[yearlyTotals.length - 1] || {};
  const baiiaMargin = lastYear.revenus ? Math.round(lastYear.baiia / lastYear.revenus * 100) : 0;
  const licorneVal = valorisation.find(v => v.annee === LICORNE_YEAR) || {};
  const y5Val = valorisation.find(v => v.annee === 'Y5') || {};

  const t = {
    fr: {
      titre: 'Résumé Exécutif',
      intro: 'EcoLunch est une plateforme SaaS + FinTech unifiée qui standardise la gestion des repas scolaires (parents, écoles, traiteurs, CSS) : commande, paiement, production, rapports, gouvernance, satisfaction et sécurité des données.',
      revenus5ans: 'Revenus 5 ans',
      gmv5ans: 'GMV 5 ans',
      margeBaiia: 'Marge BAIIA Y5',
      modeleRevenus: 'Modèle de Revenus',
      avantage: 'Avantage Compétitif',
      avantageDesc: 'Marque provinciale, hébergement et paiements 100% Québec (SonaPay, serveurs QC), RBAC adaptatif, conformité, audit.',
      investTitre: 'Investissement Demandé',
      investDesc: 'Pre-launch (Avr-Sept 2026)',
      valorisationTitre: 'Valorisation',
    },
    en: {
      titre: 'Executive Summary',
      intro: 'EcoLunch is a unified SaaS + FinTech platform that standardizes school meal management (parents, schools, caterers, school boards): ordering, payment, production, reports, governance, satisfaction, and data security.',
      revenus5ans: '5-year Revenue',
      gmv5ans: '5-year GMV',
      margeBaiia: 'EBITDA Margin Y5',
      modeleRevenus: 'Revenue Model',
      avantage: 'Competitive Advantage',
      avantageDesc: 'Provincial brand, 100% Quebec hosting & payments (SonaPay, QC servers), adaptive RBAC, compliance, audit.',
      investTitre: 'Investment Ask',
      investDesc: 'Pre-launch (Apr-Sep 2026)',
      valorisationTitre: 'Valuation',
    }
  }[language];

  const ftMin = Math.min(...Object.values(tauxParAnnee).map(r => r.ftRate * 100)).toFixed(1);
  const ftMax = Math.max(...Object.values(tauxParAnnee).map(r => r.ftRate * 100)).toFixed(1);
  const sezzMin = Math.min(...Object.values(tauxParAnnee).map(r => r.sezzleUp * 100));
  const sezzMax = Math.max(...Object.values(tauxParAnnee).map(r => r.sezzleUp * 100));

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/resume-executif" />
        <Card className="bg-black/60 border-lime-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">{t.titre}</CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-lime-500 to-cyan-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Bandeau Vertical #1 */}
            <div className="p-4 bg-gradient-to-r from-purple-500/15 to-transparent rounded-lg border border-purple-500/40" data-testid="resume-vertical-banner">
              <p className="text-sm text-purple-200">
                <span className="font-bold text-purple-300">{language === 'fr' ? 'Vertical #1 — Repas scolaires & garderies. ' : 'Vertical #1 — School & daycare meals. '}</span>
                {language === 'fr'
                  ? 'Premier vertical (et initiateur) de l\'infrastructure EcoTech Système. D\'autres verticales institutionnelles suivront post-seed.'
                  : 'First (and initiator) vertical of the EcoTech Système infrastructure. Other institutional verticals will follow post-seed.'}
              </p>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed">{t.intro}</p>

            {/* === PRÉ-SEED 500K — BLOC PRINCIPAL === */}
            <div className="p-6 bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-transparent rounded-2xl border-2 border-lime-500/40 shadow-[0_0_30px_rgba(168,213,48,0.15)]" data-testid="resume-preseed-block">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <span className="px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                    {language === 'fr' ? 'Investissement Pré-seed' : 'Pre-seed Investment'}
                  </span>
                  <h3 className="text-2xl font-bold text-white mt-2">500 000 $ CAD — ~10% equity</h3>
                  <p className="text-gray-400 text-sm">{language === 'fr' ? '6 mois (Septembre → Février) — Ronde seed planifiée Février' : '6 months (September → February) — Seed round planned February'}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'Traction validée' : 'Validated traction'}</p>
                  <p className="text-2xl font-bold text-lime-400">{tractionActuelle.traiteurs} {language === 'fr' ? 'traiteurs' : 'caterers'}</p>
                  <p className="text-xs text-gray-300">{tractionActuelle.ecoles} {language === 'fr' ? 'écoles' : 'schools'} + {tractionActuelle.garderies} {language === 'fr' ? 'garderies' : 'daycares'}</p>
                </div>
              </div>

              {/* Use of Funds */}
              <h4 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Utilisation des fonds' : 'Use of Funds'}</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {[
                  { label: 'GTM direct', val: preSeedInvestment.useOfFunds.gtm, color: 'lime', pct: '70%' },
                  { label: language === 'fr' ? 'Développement' : 'Development', val: preSeedInvestment.useOfFunds.development, color: 'cyan', pct: '10%' },
                  { label: language === 'fr' ? 'Ledger / Payout' : 'Ledger / Payout', val: preSeedInvestment.useOfFunds.ledger, color: 'orange', pct: '2%' },
                  { label: language === 'fr' ? 'Legal / Corpo' : 'Legal / Corp', val: preSeedInvestment.useOfFunds.legal, color: 'purple', pct: '3%' },
                  { label: language === 'fr' ? 'Marketing' : 'Marketing', val: preSeedInvestment.useOfFunds.marketing, color: 'cyan', pct: '2%' },
                  { label: language === 'fr' ? 'Réserve' : 'Reserve', val: preSeedInvestment.useOfFunds.reserve, color: 'orange', pct: '13%' },
                ].map((p, i) => (
                  <div key={i} className={`p-2 bg-black/40 rounded-lg border border-${p.color}-500/20`}>
                    <p className="text-[10px] text-gray-400 uppercase">{p.label}</p>
                    <p className={`text-base font-bold text-${p.color}-400`}>{(p.val / 1000).toFixed(0)}K $ <span className="text-[10px] text-gray-500">({p.pct})</span></p>
                  </div>
                ))}
              </div>

              {/* Objectifs M6 + M12 */}
              <h4 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">{language === 'fr' ? 'Objectifs Année 1' : 'Year 1 Objectives'}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">{language === 'fr' ? 'Minimum — M6 / M12' : 'Minimum — M6 / M12'}</p>
                  <p className="text-base text-cyan-400 font-bold">{objectifsY1.minimum.m6.etablissements.toLocaleString()} → {objectifsY1.minimum.m12.etablissements.toLocaleString()} {language === 'fr' ? 'établ.' : 'inst.'}</p>
                  <p className="text-xs text-gray-300">{language === 'fr' ? 'Revenu annualisé M12' : 'M12 annualized revenue'} : <span className="text-cyan-300 font-semibold">{(objectifsY1.minimum.revenuAnnualiseM12 / 1000000).toFixed(2)}M $</span></p>
                </div>
                <div className="p-3 bg-lime-500/10 rounded-lg border border-lime-500/40 shadow-[0_0_15px_rgba(132,204,22,0.15)]">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400">{language === 'fr' ? 'Réaliste — M6 / M12 (cible)' : 'Realistic — M6 / M12 (target)'}</p>
                  <p className="text-base text-lime-400 font-bold">{objectifsY1.realiste.m6.etablissements.toLocaleString()} → {objectifsY1.realiste.m12.etablissements.toLocaleString()} {language === 'fr' ? 'établ.' : 'inst.'}</p>
                  <p className="text-xs text-gray-300">{language === 'fr' ? 'Revenu annualisé M12' : 'M12 annualized revenue'} : <span className="text-lime-300 font-semibold">{(objectifsY1.realiste.revenuAnnualiseM12 / 1000000).toFixed(2)}M $</span></p>
                </div>
              </div>
            </div>

            {/* Modèle revenus */}
            <div>
              <h3 className="text-xl font-bold text-lime-400 mb-4">{t.modeleRevenus}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: 'SaaS', desc: `${SAAS_PRIX}$/${language === 'fr' ? 'traiteur' : 'caterer'}/${language === 'fr' ? 'mois' : 'month'}`, color: 'lime' },
                  { label: 'FinTech', desc: language === 'fr' ? '1,5% à 2% + 0,15$/tx' : '1.5% to 2% + $0.15/tx', color: 'cyan' },
                  { label: 'Sezzle', desc: `${(SEZZLE_NET * 100).toFixed(0)}% GMV (uplift ${sezzMin}-${sezzMax}%)`, color: 'orange' },
                  { label: language === 'fr' ? 'Abonnements' : 'Subscriptions', desc: language === 'fr' ? `2$/mois optionnel — 50% don aux écoles (${Math.round(tauxParAnnee.Y1.appTx * 100)}-${Math.round(tauxParAnnee.Y5.appTx * 100)}% parents)` : `$2/month optional — 50% school donation (${Math.round(tauxParAnnee.Y1.appTx * 100)}-${Math.round(tauxParAnnee.Y5.appTx * 100)}% parents)`, color: 'lime' },
                  { label: 'EcoLunch Connect', desc: language === 'fr' ? '2,5% en sus des frais réguliers (jumelage école-traiteur)' : '2.5% on top of regular fees (school-caterer matching)', color: 'cyan' },
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 bg-black/40 rounded-lg border border-${item.color}-500/20`}>
                    <p className={`text-sm font-semibold text-${item.color}-400`}>{item.label}</p>
                    <p className="text-xs text-gray-400 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Avantage compétitif */}
            <div className="p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border border-lime-500/30">
              <h3 className="text-xl font-bold text-lime-400 mb-3">{t.avantage}</h3>
              <p className="text-gray-300 leading-relaxed">{t.avantageDesc}</p>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
